/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu';
import Heading from '@atlaskit/heading';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import {
	deleteSessionItem,
	getSessionItem,
	setSessionItem,
} from '@atlassian/search-dialog/session-storage-utils';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { EditDefinition } from '../../../../ai-answer-dialog/definition-answer-card/edit-definition';
import { onClickDisableDefinition } from '../../analytics';
import { toggleDefinitionErrorPayload } from '../../error-analytics';
import { useToggleDefinitionSupplier } from '../../footer/useToggleDefinitionSupplier';

const AI_DEFINITION_TOGGLE_CACHE_KEY = 'atlassian.search-ai.ai-definition-toggle';

interface DefinitionToggleCacheType {
	keyPhrase?: string;
}

const messages = defineMessages({
	// messages to disable ALL definitions
	disable_definitions_more_icon_tooltip: {
		id: 'search_ai.disable_definitions_more_icon_tooltip',
		defaultMessage: 'See more actions',
		description: 'Tooltip for definition more icon',
	},
	disable_definitions_header: {
		id: 'search_ai.disable_definitions_header',
		defaultMessage: 'Don’t recommend definitions at all',
		description: 'Header text for definitions menu option to disable autohighlighting',
	},
	disable_definitions_description: {
		id: 'search_ai.disable_definitions_description',
		defaultMessage:
			'When this is on, we’ll no longer automatically highlight terms and acronyms on any page. Only you will see these changes.',
		description:
			'Description text for the reading aids menu option to not disable auto highlighting',
	},
	// messages to disable SINGLE definition
	disable_definition_term_header: {
		id: 'search_ai.disable_definition_term_header',
		defaultMessage: 'Don’t recommend this term',
		description:
			'Header text for definitions menu option to disable autohighlighting for this single term',
	},
	disable_definition_term_description: {
		id: 'search_ai.disable_definition_term_description',
		defaultMessage:
			'When this is on, we’ll no longer automatically highlight this specific term on any page. Only you will see these changes.',
		description: 'Description text for the reading aids menu option to disable single term',
	},
});

const MoreIconStyled = () => (
	<ShowMoreHorizontalIcon
		label="definitions-disable-menu-button-icon"
		color={token('color.icon.subtle')}
	/>
);

const styles = cssMap({
	root: {
		width: '305px',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	dividerStyles: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
	},
});

export const DefinitionsDropdownMenu: () => JSX.Element = () => {
	const {
		commonAttributes,
		definitionToggleEnabled,
		fireAnalyticsEvent,
		readingAidsOptions,
		sourceProduct,
	} = useAIAnswerContext();

	const [definitionsDropdownMenuOpen, setDefinitionsDropdownMenuOpen] = useState(false);
	const [disableDefinitions, setDisableDefinitions] = useState(false);

	// the localStorage method to track whether all Definitions are disabled is different
	// between products so we need to get from the product whether Definitions have already been disabled by user
	useEffect(() => {
		const definitionsDisabled =
			!!readingAidsOptions?.disableDefinitionsCallbacks?.getDefinitionsDisabled();
		setDisableDefinitions(definitionsDisabled);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { formatMessage } = useIntl();

	const disableDefinitionsCallbacks = readingAidsOptions?.disableDefinitionsCallbacks;

	// need to account for non-platformized version
	const { menuOptions: readingAidsMenuOptions } = readingAidsOptions || {};

	const { cloudId, query: keyPhrase, workspaceId } = commonAttributes;
	const cachedDefinitionTermDisabled = JSON.parse(
		getSessionItem(AI_DEFINITION_TOGGLE_CACHE_KEY) || '{}',
	) as DefinitionToggleCacheType;
	const [disableDefinitionTerm, setDisableDefinitionTerm] = useState(
		cachedDefinitionTermDisabled.keyPhrase === keyPhrase,
	);

	const { toggleDefinition } = useToggleDefinitionSupplier();
	const onToggleDefinitionTermDisable = async () => {
		try {
			setDisableDefinitionTerm((current) => !current);
			await toggleDefinition({
				workspaceId,
				keyPhrase,
				isDisabled: !disableDefinitionTerm,
				cloudId,
				product: sourceProduct ?? '',
			});
			if (disableDefinitionTerm) {
				deleteSessionItem(AI_DEFINITION_TOGGLE_CACHE_KEY);
			} else {
				fireAnalyticsEvent(onClickDisableDefinition(commonAttributes));
				setSessionItem(AI_DEFINITION_TOGGLE_CACHE_KEY, JSON.stringify({ keyPhrase }));
			}
		} catch (error: unknown) {
			setDisableDefinitionTerm((current) => !current);
			fireAnalyticsEvent(toggleDefinitionErrorPayload(error, commonAttributes));
		}
	};

	const { definitionCurationOptions } = readingAidsOptions || {};
	const editDefinitionEnabled = !!definitionCurationOptions;

	return (
		<DropdownMenu
			isOpen={definitionsDropdownMenuOpen}
			trigger={({ triggerRef, ...props }) => (
				<IconButton
					{...props}
					appearance="subtle"
					icon={MoreIconStyled}
					ref={triggerRef}
					onClick={() => {
						setDefinitionsDropdownMenuOpen(!definitionsDropdownMenuOpen);
					}}
					testId="definitions-menu-button"
					label="definitions-menu-button"
				/>
			)}
			shouldRenderToParent
		>
			<Stack xcss={styles.root} space="space.100">
				{disableDefinitionsCallbacks && (
					<Stack space="space.100">
						<Inline alignBlock="center" spread="space-between">
							<Heading size="xxsmall">{formatMessage(messages.disable_definitions_header)}</Heading>
							<Toggle
								onChange={() => {
									if (disableDefinitions) {
										disableDefinitionsCallbacks.onEnableDefinitions();
									} else {
										disableDefinitionsCallbacks.onDisableDefinitions();
									}
									setDisableDefinitions(!disableDefinitions);
								}}
								isChecked={disableDefinitions}
								testId="definitions-toggle-disable"
							/>
						</Inline>
						<Text color="color.text.subtle" size="small">
							{formatMessage(messages.disable_definitions_description)}
						</Text>
					</Stack>
				)}
				{readingAidsMenuOptions &&
					readingAidsMenuOptions.length > 0 &&
					readingAidsMenuOptions.map((option) => (
						<Stack space="space.100">
							<Inline alignBlock="center" spread="space-between">
								<Heading size="xxsmall">
									{formatMessage(messages.disable_definitions_header)}
								</Heading>
								<Toggle
									onChange={option.onChange}
									isChecked={option.toggleState}
									testId="definitions-toggle-disable"
								/>
							</Inline>
							<Text color="color.text.subtle" size="small">
								{formatMessage(messages.disable_definitions_description)}
							</Text>
						</Stack>
					))}
				{definitionToggleEnabled && (
					<Stack space="space.100">
						<Box xcss={styles.dividerStyles} />
						<Inline alignBlock="center" spread="space-between">
							<Heading size="xxsmall">
								{formatMessage(messages.disable_definition_term_header)}
							</Heading>
							<Toggle
								onChange={onToggleDefinitionTermDisable}
								isChecked={disableDefinitionTerm}
								testId="definition-term-toggle-disable"
							/>
						</Inline>
						<Text color="color.text.subtle" size="small">
							{formatMessage(messages.disable_definition_term_description)}
						</Text>
					</Stack>
				)}
				{editDefinitionEnabled && (
					<Stack space="space.100">
						<Box xcss={styles.dividerStyles} />
						<Box>
							<EditDefinition onClose={() => setDefinitionsDropdownMenuOpen(false)} />
						</Box>
					</Stack>
				)}
			</Stack>
		</DropdownMenu>
	);
};
